import { useCallback, useEffect, useRef, useState } from "react";
import type { PDFDocumentLoadingTask, PDFDocumentProxy } from "pdfjs-dist";
import { logConnectorFailure, logConnectorSuccess } from "../../../../../telemetry";
import { DETAIL_ERROR_MESSAGES, PDF_CONFIG } from "../../../constants";

// Loaded lazily (not at module scope) so the ~1MB pdfjs-dist bundle — and its
// module-level use of browser APIs like DOMMatrix — is only pulled in once a
// user actually opens the PDF tab, not whenever this file is merely imported
// (e.g. via the connector registry's module map, or in unit tests/SSR).
let pdfjsLibPromise: Promise<typeof import("pdfjs-dist")> | null = null;
function loadPdfjsLib(): Promise<typeof import("pdfjs-dist")> {
  pdfjsLibPromise ??= Promise.all([
    import("pdfjs-dist"),
    import("pdfjs-dist/build/pdf.worker.min.mjs?url"),
  ]).then(([lib, worker]) => {
    lib.GlobalWorkerOptions.workerSrc = worker.default;
    return lib;
  });
  return pdfjsLibPromise;
}

const PDF_LOAD_TIMEOUT_MS = 15_000;

/**
 * Races `promise` against a timer so a load that never settles (e.g. pdf.js
 * failing to negotiate with its worker in some host environment, silently —
 * no error, no resolution) surfaces as a normal error after `ms` instead of
 * leaving the UI on its loading spinner forever.
 */
function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

function isValidUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
}

export function usePdfManager() {
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [zoomLevel, setZoomLevel] = useState<number>(PDF_CONFIG.DEFAULT_ZOOM);
  const [pdfError, setPdfError] = useState("");
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [isPdfLoaded, setIsPdfLoaded] = useState(false);
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const isUnmountedRef = useRef(false);
  const pdfDocRef = useRef<PDFDocumentProxy | null>(null);
  // pdfjs-dist v6 moved destroy() off the resolved PDFDocumentProxy onto the
  // PDFDocumentLoadingTask getDocument() returns immediately (before its
  // .promise resolves) — this ref is what destroyPdf() actually calls.
  const loadingTaskRef = useRef<PDFDocumentLoadingTask | null>(null);

  const zoomIn = useCallback(() => setZoomLevel((prev) => Math.min(prev + PDF_CONFIG.ZOOM_STEP, PDF_CONFIG.MAX_ZOOM)), []);
  const zoomOut = useCallback(() => setZoomLevel((prev) => Math.max(prev - PDF_CONFIG.ZOOM_STEP, PDF_CONFIG.MIN_ZOOM)), []);
  const resetZoom = useCallback(() => setZoomLevel(PDF_CONFIG.DEFAULT_ZOOM), []);
  const setZoom = useCallback(
    (level: number) => setZoomLevel(Math.min(Math.max(level, PDF_CONFIG.MIN_ZOOM), PDF_CONFIG.MAX_ZOOM)),
    [],
  );

  const renderPage = useCallback(
    async (pageNum: number, canvas: HTMLCanvasElement): Promise<void> => {
      const currentPdfDoc = pdfDocRef.current;
      if (!currentPdfDoc) return;
      const page = await currentPdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale: zoomLevel * PDF_CONFIG.BASE_SCALE });
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      const context = canvas.getContext("2d");
      if (!context) return;
      context.clearRect(0, 0, canvas.width, canvas.height);
      await page.render({ canvas, canvasContext: context, viewport }).promise;
    },
    [zoomLevel],
  );

  const renderAllPages = useCallback(async (): Promise<void> => {
    if (!pdfDocRef.current || totalPages === 0) return;
    canvasRefs.current = canvasRefs.current.slice(0, totalPages);
    await Promise.all(
      Array.from({ length: totalPages }, (_, i) => {
        const canvas = canvasRefs.current[i];
        return canvas ? renderPage(i + 1, canvas) : Promise.resolve();
      }),
    );
  }, [totalPages, renderPage]);

  const destroyPdf = useCallback(async (): Promise<void> => {
    if (loadingTaskRef.current) {
      await loadingTaskRef.current.destroy();
      loadingTaskRef.current = null;
    }
  }, []);

  const loadPdf = useCallback(
    async (url: string): Promise<void> => {
      if (!isValidUrl(url)) {
        setPdfError(DETAIL_ERROR_MESSAGES.INVALID_PDF_URL);
        return;
      }

      setIsLoadingPdf(true);
      setPdfError("");

      try {
        if (loadingTaskRef.current) await destroyPdf();

        // The whole chain — lazily loading the ~1MB pdfjs-dist chunk and its
        // worker script (a dynamic import, not covered by getDocument's own
        // promise) *and* the document load itself — is under one timeout, so
        // a hang anywhere in it (not just inside pdf.js's own network layer)
        // surfaces as a retriable error instead of an silent, permanent spinner.
        const pdf = await withTimeout(
          loadPdfjsLib().then((pdfjsLib) => {
            // pdf.js needs three self-hosted asset directories at runtime —
            // none of these are optional in a self-hosted (non-CDN) setup:
            //   - wasmUrl: WASM image codecs (JBIG2/OpenJPEG/qcms), used to
            //     decode scanned-page images. Without it, decoding fails
            //     per-image with a console *warning* (not a rejection), so
            //     the page "renders" successfully but blank.
            //   - cMapUrl (+ cMapPacked): predefined Adobe CMaps, needed for
            //     non-Latin/complex-script text (e.g. CJK) that uses
            //     non-standard character encodings.
            //   - standardFontDataUrl: fallback glyph data for standard
            //     fonts (e.g. Helvetica) that aren't embedded in the PDF.
            // Missing cMapUrl/standardFontDataUrl fail the same silent way
            // as a missing wasmUrl did — no error, just missing content.
            // vite.config.ts copies all three out of node_modules/pdfjs-dist
            // at build/dev time so they're served at these stable paths.
            const origin = window.location.origin;
            const loadingTask = pdfjsLib.getDocument({
              url,
              wasmUrl: new URL("/assets/wasm/", origin).href,
              cMapUrl: new URL("/assets/cmaps/", origin).href,
              cMapPacked: true,
              standardFontDataUrl: new URL("/assets/standard_fonts/", origin).href,
            });
            loadingTaskRef.current = loadingTask;
            return loadingTask.promise;
          }),
          PDF_LOAD_TIMEOUT_MS,
          "PDF load timed out",
        );

        if (isUnmountedRef.current) {
          await loadingTaskRef.current?.destroy();
          return;
        }

        pdfDocRef.current = pdf;
        setPdfDoc(pdf);
        setTotalPages(pdf.numPages);
        setIsPdfLoaded(true);
        logConnectorSuccess("docuparser", "pdf-load");
      } catch (err) {
        if (isUnmountedRef.current) return;
        const isTimeout = err instanceof Error && err.message === "PDF load timed out";
        setPdfError(isTimeout ? "Loading the PDF timed out. Please retry." : DETAIL_ERROR_MESSAGES.PDF_LOAD_FAILED);
        // Console as well as telemetry. The message shown to the user is a
        // fixed string, and logConnectorFailure's only sink is the telemetry
        // ingest endpoint — which 401s in the deployed dev client, so the real
        // pdf.js error (blob 403, InvalidPDFException, PasswordException on an
        // encrypted source, worker fault) had nowhere observable to land.
        // Deliberately logs the error, never `url`: that carries a live SAS token.
        console.error("[docuparser] PDF load failed:", err);
        logConnectorFailure("docuparser", err, isTimeout ? "pdf-load-timeout" : "pdf-load-failed");
      } finally {
        if (!isUnmountedRef.current) setIsLoadingPdf(false);
      }
    },
    [destroyPdf],
  );

  const resetPdf = useCallback(() => {
    destroyPdf();
    pdfDocRef.current = null;
    setPdfDoc(null);
    setTotalPages(0);
    setZoomLevel(PDF_CONFIG.DEFAULT_ZOOM);
    setPdfError("");
    setIsPdfLoaded(false);
  }, [destroyPdf]);

  useEffect(() => {
    // Reset on every (re)mount, not just once at useRef's initial value — React
    // StrictMode intentionally mounts, cleans up, and re-mounts every component
    // once in dev mode to catch exactly this class of bug. Without resetting
    // here, the cleanup's `= true` below is never undone on the real mount that
    // follows, so it stays permanently stuck `true` — every `if
    // (isUnmountedRef.current) return;` guard in loadPdf (including the one
    // that clears isLoadingPdf in its `finally`) then silently no-ops forever,
    // regardless of whether the load actually succeeds, fails, or times out.
    isUnmountedRef.current = false;
    return () => {
      isUnmountedRef.current = true;
      destroyPdf();
    };
  }, [destroyPdf]);

  return {
    pdfDoc,
    totalPages,
    zoomLevel,
    pdfError,
    isLoadingPdf,
    isPdfLoaded,
    canvasRefs,
    zoomIn,
    zoomOut,
    resetZoom,
    setZoom,
    renderAllPages,
    loadPdf,
    resetPdf,
  };
}
